/**
 * What the planning panel says about a fill-gaps answer, as pure functions over it: the three
 * option cards, the shifts as the chosen proposal would leave them, the «Ny» and «Endret» marks and
 * the before-and-after list. Every number is the server's (cost, coverage, findings); nothing here
 * prices or judges a plan.
 */
import type { ApiShift } from '@/api/shifts';
import { T } from '@/i18n';
import { type Entry, oslo, ruleText } from '@/rules';
import { DN, MONTHS } from '@/ui/format';
import { dow } from '@timefly/core';
import type { Ack, BasisLine, Cost, Finding, Objective, Op, Preview, Proposal } from './api';

export const NAMES: Record<Objective, { label: string; about: string }> = {
  lower_cost: { label: 'Lavest kostnad', about: 'Den rimeligste ledige personen på hver vakt.' },
  history_similarity: {
    label: 'Som tidligere uker',
    about: 'De som pleier å jobbe de dagene, siste fire uker.',
  },
  weekend_balance: {
    label: 'Fordel helgen jevnt',
    about: 'Helgevaktene til dem med færrest helger siste fire uker.',
  },
};

export const kr = (minor: number) => `${Math.round(minor / 100).toLocaleString('nb-NO')} kr`;
const hrs = (h: number) => `${h.toLocaleString('nb-NO', { maximumFractionDigits: 1 })} t`;
/** «Man 19. okt.» */
export const dayLabel = (date: string) =>
  `${DN[dow(date)]} ${Number(date.slice(8, 10))}. ${(MONTHS[Number(date.slice(5, 7)) - 1] ?? '').slice(0, 3)}.`;
const clock = (iso: string) => oslo(iso)?.time ?? '';
export const span = (a: string, b: string) => `${clock(a)}–${clock(b)}`;

type Say = (key: string, values?: Record<string, string | number>) => string;

/** One validator or eligibility finding in the words the grid uses for the same code. */
export function findingText(say: Say, f: Finding): string {
  if (f.source === 'eligibility') return f.text;
  const m = f.measured;
  const n = (k: string) => (typeof m[k] === 'number' ? (m[k] as number) : 0);
  const e: Entry = {
    code: f.code,
    sev: f.severity === 'danger' ? 'd' : 'w',
    args: {
      hours: n('hours'),
      minutes: n('minutes'),
      max: n('max'),
      ...(f.code === 'weekrest' ? { gap: n('hours') } : {}),
      ...(typeof m.from === 'string' ? { from: Date.parse(`${m.from}T12:00:00Z`) } : {}),
    },
    source: 'api',
    text: f.text,
  };
  return ruleText(say, e);
}

/** Why a proposal is not the whole answer, in one sentence, or null when it is. */
export function incompleteOf(p: Proposal): string | null {
  const why = reasonsOf(p);
  if (!why.length) return null;
  return `${T('Ufullstendig fordi')} ${why.join(T(' og '))}.`;
}

/** The reasons a proposal is not the whole answer, each a clause after «fordi». */
export function reasonsOf(p: Proposal): string[] {
  const why: string[] = [];
  const o = p.outcome;
  if (o.status === 'timeout') why.push(T('tiden gikk ut før forslaget var ferdig'));
  if (o.status === 'unknown' && p.cost.visibility === 'restricted' && p.objective === 'lower_cost')
    why.push(T('du har ikke tilgang til lønn, så kostnaden kan ikke sammenlignes'));
  else if (o.status === 'unknown') why.push(T('det er ingen ledige vakter å fylle'));
  if (p.coverage.covered < p.coverage.required && o.status !== 'timeout' && o.status !== 'unknown')
    why.push(T('ingen kunne ta {n} av vaktene', { n: p.coverage.required - p.coverage.covered }));
  const miss = p.input_completeness.missing;
  const avail = miss.filter((x) => x.input === 'availability').length;
  if (avail) why.push(T('tilgjengelighet ikke er oppgitt for {n} av vaktene', { n: avail }));
  const birth = miss.filter((x) => x.input === 'birth_date').length;
  if (birth) why.push(T('fødselsdato mangler for {n} personer', { n: birth }));
  const rate = miss.filter((x) => x.input === 'rate' && x.ref.user_id != null).length;
  if (rate) why.push(T('timelønn mangler for {n} personer', { n: rate }));
  if (miss.some((x) => x.input === 'validator')) {
    const reasons = new Set((p.findings.not_checked ?? []).map((x) => x.reason));
    // the year's overtime total counts the stored plan, so a shift given a person here is not in it yet
    if (reasons.size === 1 && reasons.has('annual_overtime_not_recomputed'))
      why.push(T('overtid over året ikke er regnet med de nye vaktene'));
    else why.push(T('regelsjekken ikke kjørte for alle vakter'));
  }
  return why;
}

export interface Option {
  id: string;
  objective: Objective;
  revision: string;
  label: string;
  about: string;
  totalMinor: number | null;
  hours: number | null;
  /** kroner over the cheapest of the three; 0 on the cheapest; null without pay */
  overMinor: number | null;
  fills: number;
  open: number;
  warnings: string[];
  /** the same warnings by person, most first, each rule once with its days (owner 18.10: 43 loose lines were unreadable) */
  byPerson: WarnGroup[];
  /** warnings the week carried before this option, counted once each */
  existing: number;
  incomplete: string | null;
  /** the same reasons as clauses, so what every option shares can be said once */
  reasons: string[];
  sameAs: string | null;
  proposal: Proposal;
  /** shifts given to another person than the first option gives them (owner 18.10: three cards looked alike) */
  differs: number;
  /** who this option puts on the weekend, first names */
  weekend: string[];
}

/** A finding this option brought or made worse; what the saved week already said is not its doing (owner 18.10). */
export const brought = (f: Finding) => f.in_period && f.new !== false;

export type WarnGroup = { who: string; n: number; items: { text: string; days: string[] }[] };
const cap = (s: string) => (s ? s[0]?.toUpperCase() + s.slice(1) : s);
/** «søn 18. okt.» as «søn 18.»: the week is on screen already */
const shortDay = (date: string) =>
  dayLabel(date)
    .toLowerCase()
    .replace(/\s+[^\s\d]+\.?$/, '');

const total = (c: Cost) => (c.visibility === 'visible' && c.total_minor != null ? c.total_minor : null);

/** The three cards, in the server's order. */
export function optionsOf(data: Preview, say: Say): Option[] {
  const firstName = new Map(data.context.people.map((p) => [p.user_id, p.name.split(' ')[0] ?? p.name]));
  const priced = data.proposals.map((p) => total(p.cost)).filter((x): x is number => x != null);
  const cheapest = priced.length ? Math.min(...priced) : null;
  const dupOf = new Map(data.candidates.map((c) => [c.candidate_id, c.duplicate_of]));
  const labelOf = (id: string) => {
    const p = data.proposals.find((x) => x.origin.candidate_id === id);
    return p ? T(NAMES[p.objective].label) : id;
  };
  // who each option gives each shift, by the shift's key, to say how the options differ
  const keyOf = (o: Op) => (o.target.shift_id != null ? `s${o.target.shift_id}` : `c${o.target.client_id}`);
  const whoOf = (p: Proposal) => new Map(p.operations.map((o) => [keyOf(o), o.after?.user_id ?? null]));
  const firstWho = data.proposals[0] ? whoOf(data.proposals[0]) : new Map<string, number | null>();
  return data.proposals.map((p) => {
    const t = total(p.cost);
    // the validator says some codes twice on one shift (planned and actual); one line each
    const seen = new Set<string>();
    const warnings: string[] = [];
    const groups = new Map<string, WarnGroup>();
    // what the week already said before this option is counted, not listed (owner 18.10)
    const had = new Set<string>();
    for (const f of p.findings.items) {
      if (f.in_period && !brought(f)) had.add(`${f.user_id}|${f.date}|${findingText(say, f)}`);
      if (!brought(f)) continue;
      const who = f.user_id != null ? cap(firstName.get(f.user_id) ?? '') : T('Ledig vakt');
      const text = findingText(say, f);
      const line = `${who}, ${dayLabel(f.date).toLowerCase()}: ${text}`;
      if (seen.has(line)) continue;
      seen.add(line);
      warnings.push(line);
      const g = groups.get(who) ?? { who, n: 0, items: [] };
      groups.set(who, g);
      g.n += 1;
      const item = g.items.find((x) => x.text === text) ?? { text, days: [] };
      if (!g.items.includes(item)) g.items.push(item);
      item.days.push(f.date);
    }
    // days in date order, each once, as «man 12.»
    for (const g of groups.values())
      for (const x of g.items) x.days = [...new Set(x.days)].sort().map(shortDay);
    const byPerson = [...groups.values()].sort((a, b) => b.n - a.n || a.who.localeCompare(b.who, 'nb'));
    const dup = dupOf.get(p.origin.candidate_id);
    return {
      id: p.origin.candidate_id,
      objective: p.objective,
      revision: p.revision,
      label: T(NAMES[p.objective].label),
      about: T(NAMES[p.objective].about),
      totalMinor: t,
      hours: p.cost.hours,
      overMinor: t != null && cheapest != null ? t - cheapest : null,
      fills: p.coverage.covered,
      open: p.coverage.required,
      warnings,
      byPerson,
      existing: had.size,
      incomplete: incompleteOf(p),
      reasons: reasonsOf(p),
      sameAs: dup ? labelOf(dup) : null,
      proposal: p,
      differs: [...whoOf(p)].filter(([k, u]) => firstWho.get(k) !== u).length,
      weekend: [
        ...new Set(
          p.operations
            .filter((o) => {
              const d = o.after?.user_id != null ? oslo(o.after.start_time)?.date : null;
              return !!d && dow(d) >= 5; // Saturday and Sunday in Oslo
            })
            .map((o) => cap(firstName.get(o.after?.user_id as number) ?? '')),
        ),
      ].sort((a, b) => a.localeCompare(b, 'nb')),
    };
  });
}

/** What a shift becomes: an open one that gets a person is new to that person; anything else changed. */
export const markOf = (op: Op): 'new' | 'changed' =>
  op.kind === 'create' || (op.before?.user_id == null && op.after?.user_id != null) ? 'new' : 'changed';

export function marksOf(p: Proposal | null): Map<number, 'new' | 'changed'> {
  const out = new Map<number, 'new' | 'changed'>();
  if (!p) return out;
  p.operations.forEach((op, i) => {
    const id = op.target.shift_id ?? -(i + 1);
    if (op.kind !== 'delete') out.set(id, markOf(op));
  });
  return out;
}

export const countsOf = (p: Proposal) => {
  const m = [...marksOf(p).values()];
  return { added: m.filter((x) => x === 'new').length, changed: m.filter((x) => x === 'changed').length };
};

/** «3 nye, 1 endret» */
export const countsText = (p: Proposal) => {
  const c = countsOf(p);
  return [c.added ? T('{n} nye', { n: c.added }) : null, c.changed ? T('{n} endret', { n: c.changed }) : null]
    .filter(Boolean)
    .join(', ');
};

/**
 * The saved shifts as the proposal would leave them: a person on an open shift, a new draft for a
 * create. A created shift has a negative id, so nothing can write to it before it is saved.
 */
export function overlay<U extends { id: number }>(
  shifts: ApiShift[],
  p: Proposal | null,
  users: U[],
): ApiShift[] {
  if (!p) return shifts;
  const userOf = (id: number | null) => (id == null ? null : (users.find((u) => u.id === id) ?? null));
  const byId = new Map(
    p.operations.filter((o) => o.target.shift_id != null).map((o) => [o.target.shift_id, o]),
  );
  const out = shifts.map((s) => {
    const op = byId.get(s.id);
    if (!op?.after) return s;
    return { ...s, ...op.after, user: userOf(op.after.user_id) as ApiShift['user'] };
  });
  p.operations.forEach((op, i) => {
    if (op.kind !== 'create' || !op.after) return;
    const like = shifts[0];
    out.push({
      ...(like ?? ({} as ApiShift)),
      ...op.after,
      id: -(i + 1),
      published: false,
      is_approved: false,
      punch_in_time: null,
      punch_out_time: null,
      lunch_in_time: null,
      lunch_out_time: null,
      is_swap_requested: false,
      is_offer_requested: false,
      shiftType: null,
      validation_errors: [],
      taskAssignments: [],
      user: userOf(op.after.user_id) as ApiShift['user'],
    });
  });
  return out;
}

export interface ChangeRow {
  key: string;
  at: string;
  title: string;
  mark: 'new' | 'changed';
  before: string;
  after: string;
}

/** «Man 19. okt., Kveld», with Før and Etter in words. */
export function changesOf(p: Proposal, data: Preview, typeName: (id: number | null) => string): ChangeRow[] {
  const name = new Map(data.context.people.map((x) => [x.user_id, x.name]));
  const who = (id: number | null | undefined) =>
    id == null ? T('Ledig vakt') : (name.get(id) ?? T('Ukjent'));
  return p.operations
    .filter((o) => o.after)
    .map((o) => {
      const a = o.after as NonNullable<Op['after']>;
      const date = oslo(a.start_time)?.date ?? '';
      return {
        key: o.op_id,
        at: a.start_time,
        title: `${dayLabel(date)}, ${typeName(a.shift_type_id)}`,
        mark: markOf(o),
        before: o.before
          ? `${who(o.before.user_id)}, ${span(o.before.start_time, o.before.end_time)}`
          : T('Ingen vakt'),
        after: `${who(a.user_id)}, ${span(a.start_time, a.end_time)}`,
      };
    })
    .sort((x, y) => Date.parse(x.at) - Date.parse(y.at));
}

/** «22 360 kr → 27 830 kr» and «88 → 111 t», or null where the reader may not see pay. */
export function beforeAfter(before: Cost, after: Cost) {
  const b = total(before);
  const a = total(after);
  return {
    pay: b != null && a != null ? `${kr(b)} → ${kr(a)}` : null,
    payDelta: b != null && a != null ? a - b : null,
    hours: `${(before.hours ?? 0).toLocaleString('nb-NO')} → ${hrs(after.hours ?? 0)}`,
  };
}

/** «Man 23. nov., 08:00–16:00, Kafévakt, 2 vakter»; a left-out pattern has a weekday and no date. */
export function basisLine(l: Partial<BasisLine> & { from?: string; to?: string }, weekday?: number): string {
  const day = l.date ? dayLabel(l.date) : weekday != null ? (DN[weekday - 1] ?? '') : '';
  return [
    day,
    `${l.from}–${l.to}`,
    l.type_name,
    l.count && l.count > 1 ? T('{n} vakter', { n: l.count }) : null,
  ]
    .filter(Boolean)
    .join(', ');
}

/**
 * A basis as the prototype sums a pattern up: one line per time and type, with its days.
 * «Åpning, 07:00–15:00, alle dager», «Kveld, 15:00–23:00, man, tir, fre, 2 per dag».
 */
export function basisSummary(lines: BasisLine[]): string[] {
  const by = new Map<string, { l: BasisLine; days: Set<number> }>();
  for (const l of lines) {
    const k = `${l.from}|${l.to}|${l.type_name ?? ''}|${l.count}`;
    const g = by.get(k) ?? { l, days: new Set<number>() };
    g.days.add(dow(l.date));
    by.set(k, g);
  }
  return [...by.values()].map(({ l, days }) => {
    const d = [...days].sort((a, b) => a - b);
    const when = d.length === 7 ? T('alle dager') : d.map((i) => (DN[i] ?? '').toLowerCase()).join(', ');
    return [l.type_name, `${l.from}–${l.to}`, when, l.count > 1 ? T('{n} per dag', { n: l.count }) : null]
      .filter(Boolean)
      .join(', ');
  });
}

/** Weekday indexes (0 Monday) as short runs: «man–tor», «fre–søn», «man, ons»; seven is «alle dager». */
export function dayRanges(d: number[]): string[] {
  if (d.length === 7) return [T('alle dager')];
  const name = (i: number) => (DN[i] ?? '').toLowerCase();
  const out: string[] = [];
  for (let i = 0; i < d.length; i += 1) {
    let j = i;
    while (j + 1 < d.length && (d[j + 1] ?? -9) === (d[j] ?? -9) + 1) j += 1;
    const a = d[i] ?? 0;
    const b = d[j] ?? 0;
    out.push(j - i >= 2 ? `${name(a)}–${name(b)}` : j > i ? `${name(a)}, ${name(b)}` : name(a));
    i = j;
  }
  return out;
}

/** The same summary as rows for a list (owner 18.10: grey text lines were hard to read). */
export function basisRows(
  lines: BasisLine[],
): { key: string; type: string; time: string; days: string[]; perDay: number }[] {
  const by = new Map<string, { l: BasisLine; days: Set<number> }>();
  for (const l of lines) {
    const k = `${l.from}|${l.to}|${l.type_name ?? ''}|${l.count}`;
    const g = by.get(k) ?? { l, days: new Set<number>() };
    g.days.add(dow(l.date));
    by.set(k, g);
  }
  return [...by.entries()].map(([key, { l, days }]) => {
    const d = [...days].sort((a, b) => a - b);
    return {
      key,
      type: l.type_name ?? T('Vakt'),
      time: `${l.from}–${l.to}`,
      days: dayRanges(d),
      perDay: l.count,
    };
  });
}

export interface Assessment {
  key: string;
  /** every finding id the line stands for (the validator can say one thing twice, planned and actual) */
  ids: string[];
  severity: 'danger' | 'warning';
  text: string;
  state: 'open' | 'acknowledged';
  /** acknowledged once, then the facts changed */
  reopened: boolean;
  reason: string | null;
}

/**
 * The proposal's findings for the «N må vurderes» box, one line per thing said, with the
 * manager's acknowledgements laid over them as the server lays them (adapter.acknowledge): bound to
 * the exact facts (fingerprint), reopened when they change, never closing a danger.
 */
export function assess(p: Proposal, data: Preview, acks: Record<string, Ack>, say: Say): Assessment[] {
  const firstName = new Map(data.context.people.map((x) => [x.user_id, x.name.split(' ')[0] ?? x.name]));
  const by = new Map<string, Assessment>();
  for (const f of p.findings.items) {
    if (!brought(f)) continue;
    const text = `${f.user_id != null ? `${firstName.get(f.user_id) ?? ''}, ` : ''}${dayLabel(f.date).toLowerCase()}: ${findingText(say, f)}`;
    const a = acks[f.finding_id];
    const held = !!a && a.fingerprint === f.fingerprint && f.severity === 'warning';
    const line = by.get(text) ?? {
      key: text,
      ids: [],
      severity: f.severity,
      text,
      state: 'acknowledged',
      reopened: false,
      reason: null,
    };
    line.ids.push(f.finding_id);
    if (f.severity === 'danger') line.severity = 'danger';
    if (!held) line.state = 'open';
    if (a && !held && f.severity === 'warning') line.reopened = true;
    if (held) line.reason = a?.reason ?? null;
    by.set(text, line);
  }
  return [...by.values()];
}

/**
 * The comparison as the prototype words it (planning.js compareBody): what every option shares, said
 * once under the list, and what is left for each option alone.
 */
export function compareOf(options: Option[]) {
  const common = options.length
    ? (options[0]?.reasons ?? []).filter((r) => options.every((o) => o.reasons.includes(r)))
    : [];
  return {
    common,
    own: (o: Option) => o.reasons.filter((r) => !common.includes(r)),
  };
}
